import type { TalentQualificationStatus } from "@asap/shared/utils/supabase.enum";
import type { Talent } from "@asap/shared/utils/supabase.types";
import { useAtsSearchTrackingContext } from "~/components/sourcing/composables/useAtsSearchTrackingContext";
import { usePosthog } from "~/composables/usePosthog";

export const useTalentEvents = function () {
  const posthog = usePosthog();
  const atsSearchTrackingContext = useAtsSearchTrackingContext();

  /** Outside the ATS results page there is no search: the event then carries no `source` and is not attributable. */
  const getAtsSearchProperties = () => {
    const context = atsSearchTrackingContext?.value;
    if (!context) return {};
    return {
      source: "ats_search_results",
      search_id: context.searchId,
      saved_search_id: context.savedSearchId,
      mission_id: context.missionId,
    };
  };

  /**
   * Events flow on talent qualification status changes.
   *
   * Events when :
   * - *any status* to final status
   * - *not_qualified* to *qualification_in_progress*
   * - *not_qualified* to final status
   *
   * Events properties are as example:
   * ```ts
   * posthog.trackEvent("talent_qualification_example", {
   *   talent_id: talentId,
   *   old_status: talent.qualification_status,
   *   new_status: newStatus,
   * });
   * ```
   */
  const qualificationStatusCaptureEvents = (
    talent: Pick<Talent, "id" | "qualification_status">,
    newStatus: TalentQualificationStatus
  ) => {
    const oldStatus = talent.qualification_status;

    if (newStatus === "qualified" || newStatus === "disqualified") {
      posthog.trackEvent("talent_qualification_completed", {
        talent_id: talent.id,
        old_status: oldStatus,
        new_status: newStatus,
      });
      return;
    }

    if (newStatus === "qualification_in_progress" && (oldStatus === "pre_qualified" || oldStatus === "not_qualified")) {
      posthog.trackEvent("talent_qualification_started", {
        talent_id: talent.id,
        old_status: oldStatus,
        new_status: newStatus,
      });
      return;
    }
  };

  const dereferenceCaptureEvent = (talentId: string, comment: string) => {
    posthog.trackEvent("talent_dereferenced", {
      talent_id: talentId,
      dereference_reason: comment,
    });
  };

  const captureTalentProfileViewed = (talentId: string) => {
    posthog.trackEvent("talent_profile_viewed", { ...getAtsSearchProperties(), talent_id: talentId });
  };

  const captureTalentResumeHovered = (talentId: string) => {
    posthog.trackEvent("talent_resume_hovered", { ...getAtsSearchProperties(), talent_id: talentId });
  };

  const captureTalentShortlisted = (params: { talentIds: string[]; missionIds: string[]; source?: "mission" }) => {
    posthog.trackEvent("talent_shortlisted", {
      ...getAtsSearchProperties(),
      ...(params.source ? { source: params.source } : {}),
      talent_ids: params.talentIds,
      mission_ids: params.missionIds,
    });
  };

  const captureTalentHiringPoolAdded = (params: { talent_ids: string[]; hiring_pool_ids: string[] }) => {
    posthog.trackEvent("talent_hiring_pool_added", { ...getAtsSearchProperties(), ...params });
  };

  return {
    qualificationStatusCaptureEvents,
    dereferenceCaptureEvent,
    captureTalentProfileViewed,
    captureTalentResumeHovered,
    captureTalentShortlisted,
    captureTalentHiringPoolAdded,
  };
};
